import React, { useState } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  MenuItem,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { createCommitment } from "../../api/commitments";
import PlanIntelligenceSuggestion from "./PlanIntelligenceSuggestion";
import PendingActionNotice from "../common/PendingActionNotice";

const ACTIVE_DAY_OPTIONS = [
  { label: "M", value: 0 },
  { label: "T", value: 1 },
  { label: "W", value: 2 },
  { label: "T", value: 3 },
  { label: "F", value: 4 },
  { label: "S", value: 5 },
  { label: "S", value: 6 },
];

const ALL_ACTIVE_DAYS = ACTIVE_DAY_OPTIONS.map((day) => day.value);

function CommitmentCreateDialog({ goalId, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [frequency, setFrequency] = useState("daily");
  const [minimumVersionTitle, setMinimumVersionTitle] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [doneCriteria, setDoneCriteria] = useState("");
  const [cueAnchor, setCueAnchor] = useState("");
  const [cueContext, setCueContext] = useState("");
  const [cueLocationLabel, setCueLocationLabel] = useState("");
  const [activeDays, setActiveDays] = useState(ALL_ACTIVE_DAYS);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [titleError, setTitleError] = useState("");

  async function handleSave() {
    setLoading(true);
    setErrorMessage("");
    setTitleError("");

    try {
      await createCommitment({
        goal: goalId,
        title: title.trim(),
        frequency,
        active_days: frequency === "daily" ? activeDays : ALL_ACTIVE_DAYS,
        minimum_version_title: minimumVersionTitle.trim(),
        preferred_time: preferredTime || null,
        done_criteria: doneCriteria.trim(),
        cue_anchor: cueAnchor.trim(),
        cue_context: cueContext.trim(),
        cue_location_label: cueLocationLabel.trim(),
      });
      setLoading(false);
      onSaved?.();
      onClose();
    } catch (error) {
      setLoading(false);
      setErrorMessage(error.message || "Unable to add habit.");
      const fieldError = error?.errors?.title;
      if (Array.isArray(fieldError) && fieldError.length) {
        setTitleError(fieldError[0]);
      }
    }
  }

  return (
    <Dialog open onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add a habit</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {loading ? (
            <PendingActionNotice
              compact
              title="Adding habit"
              body="Creating this habit and refreshing your plan."
            />
          ) : null}

          <TextField
            label="What will you do?"
            placeholder="e.g. Read for 20 minutes"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            fullWidth
            helperText={titleError || " "}
            error={Boolean(titleError)}
          />

          <TextField
            select
            label="How often?"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            helperText=" "
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
          </TextField>
          <TextField
            label="Keep-alive step"
            placeholder="Walk for 3 minutes"
            value={minimumVersionTitle}
            onChange={(e) => setMinimumVersionTitle(e.target.value)}
            helperText="Optional. This keeps the habit alive on low-energy days."
            fullWidth
          />
          <TextField
            label="What counts as done?"
            placeholder="e.g. Walk outside for at least 20 minutes"
            value={doneCriteria}
            onChange={(e) => setDoneCriteria(e.target.value)}
            helperText="Make the finish line clear so you and your TrustBuds know what counted."
            fullWidth
          />
          <TextField
            label="Planned time"
            type="time"
            value={preferredTime}
            onChange={(e) => setPreferredTime(e.target.value)}
            helperText="TrustBud will check in if this time passes and it is not done."
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Routine cue"
            placeholder="After breakfast"
            value={cueAnchor}
            onChange={(e) => setCueAnchor(e.target.value)}
            helperText="Optional. Use an existing routine as the trigger."
            fullWidth
          />
          <TextField
            label="Place or situation cue"
            placeholder="At my desk, after school drop-off"
            value={cueContext}
            onChange={(e) => setCueContext(e.target.value)}
            helperText="Optional. Name the context where this habit usually starts."
            fullWidth
          />
          <TextField
            label="Saved place cue"
            placeholder="Gym, office, kitchen table"
            value={cueLocationLabel}
            onChange={(e) => setCueLocationLabel(e.target.value)}
            helperText="Optional. Web saves the place label; iPhone handles device reminders."
            fullWidth
          />
          <PlanIntelligenceSuggestion
            itemType="commitment"
            payload={{ title, frequency }}
            applyLabel="Use suggested habit"
            onApply={(suggestion) => {
              if (suggestion.suggested_title) setTitle(suggestion.suggested_title);
            }}
          />

          {frequency === "daily" && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                Which days does this habit apply?
              </Typography>
              <ToggleButtonGroup
                value={activeDays}
                onChange={(_, next) => {
                  if (!next.length) return;
                  setActiveDays(next);
                }}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    borderColor: "divider",
                    color: "text.secondary",
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    bgcolor: "text.primary",
                    color: "common.white",
                    borderColor: "text.primary",
                    "&:hover": {
                      bgcolor: "text.primary",
                    },
                  },
                }}
              >
                {ACTIVE_DAY_OPTIONS.map((day) => (
                  <ToggleButton key={day.value} value={day.value}>
                    {day.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary">
                Inactive days won't count against your streak.
              </Typography>
            </Stack>
          )}

          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!title.trim() || loading}
        >
          {loading ? "Adding..." : "Add habit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CommitmentCreateDialog;
